import { useCallback, useEffect, useRef } from 'react'
import type { MutableRefObject, RefObject } from 'react'
import type { AgGridReact } from 'ag-grid-react'
import type {
  CellClassParams,
  CellFocusedEvent,
  CellMouseDownEvent,
  CellMouseOverEvent,
  CellStyle,
} from 'ag-grid-community'
import type { TableRow } from './types'

export function useTableSelection(
  gridRef: RefObject<AgGridReact<TableRow>>,
  containerRef: RefObject<HTMLDivElement>,
  /** Вызывается когда фокус попадает на последнюю строку — нужно добавить пустую */
  onFocusAtEnd: (rowIndex: number, totalRows: number) => void,
): {
  selRef: MutableRefObject<{ r1: number; r2: number; c1: number; c2: number } | null>
  extraCellsRef: MutableRefObject<Set<string>>
  anchorRef: MutableRefObject<{ row: number; col: number } | null>
  dragStart: MutableRefObject<{ row: number; col: number } | null>
  dragging: MutableRefObject<boolean>
  getColIdx: (colId: string) => number
  refreshSel: () => void
  getCellStyle: (params: CellClassParams<TableRow>) => CellStyle
  onCellMouseDown: (e: CellMouseDownEvent<TableRow>) => void
  onCellMouseOver: (e: CellMouseOverEvent<TableRow>) => void
  onCellFocused: (e: CellFocusedEvent) => void
} {
  const selRef = useRef<{ r1: number; r2: number; c1: number; c2: number } | null>(null)
  const extraCellsRef = useRef<Set<string>>(new Set())
  const dragStart = useRef<{ row: number; col: number } | null>(null)
  const dragging = useRef(false)
  const anchorRef = useRef<{ row: number; col: number } | null>(null)
  const isShiftSelecting = useRef(false)
  // true когда последний mousedown был с Ctrl/Cmd — выставляется ДО AG Grid onCellFocused
  const isCtrlSelecting = useRef(false)
  // true если mouseup завершил drag-выделение — предотвращает сброс selRef в onCellFocused
  const wasDraggingRef = useRef(false)

  // Индекс колонки по colId в актуальном порядке колонок грида.
  // Раньше список был хардкодом (COL_FIELDS), но теперь колонки
  // закупщиков строятся динамически — берём порядок из API.
  const getColIdx = useCallback(
    (colId: string) => {
      const cols = gridRef.current?.api.getColumns()
      if (!cols) return -1
      return cols.findIndex((c) => c.getColId() === colId)
    },
    [gridRef],
  )

  const refreshSel = useCallback(() => {
    gridRef.current?.api.refreshCells({ force: true })
  }, [gridRef])

  const getCellStyle = useCallback(
    (params: CellClassParams<TableRow>): CellStyle => {
      const base: CellStyle = { borderRight: '1px solid #d1d5db' }
      if (params.rowIndex == null) return base
      const c = getColIdx(params.column.getColId())
      const sel = selRef.current
      const inRect =
        sel != null &&
        params.rowIndex >= sel.r1 &&
        params.rowIndex <= sel.r2 &&
        c >= sel.c1 &&
        c <= sel.c2
      const inExtra = extraCellsRef.current.has(`${params.rowIndex}:${c}`)
      return inRect || inExtra ? { ...base, background: '#bfdbfe' } : base
    },
    [getColIdx],
  )

  // FIX: перехватываем mousedown в capture-фазе — раньше чем AG Grid меняет фокус и вызывает onCellFocused
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      isCtrlSelecting.current = e.ctrlKey || e.metaKey
    }
    document.addEventListener('mousedown', onMouseDown, true)
    return () => document.removeEventListener('mousedown', onMouseDown, true)
  }, [])

  // mouseup в capture-фазе — раньше чем AG Grid, чтобы wasDraggingRef встал до onCellFocused
  useEffect(() => {
    const up = () => {
      if (dragging.current) wasDraggingRef.current = true
      dragging.current = false
      dragStart.current = null
    }
    document.addEventListener('mouseup', up, true)
    return () => document.removeEventListener('mouseup', up, true)
  }, [])

  // ── Автоскролл при drag-выделении ──────────────────────────────────────────
  const autoScrollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mousePosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current != null) {
      clearInterval(autoScrollRef.current)
      autoScrollRef.current = null
    }
  }, [])

  const updateSelectionFromPos = useCallback(
    (x: number, y: number) => {
      if (!dragStart.current) return
      const container = containerRef.current
      if (!container) return
      const cellArea =
        (container.querySelector('.ag-center-cols-viewport') as HTMLElement | null) ??
        (container.querySelector('.ag-body-viewport') as HTMLElement | null)
      if (!cellArea) return
      const vr = cellArea.getBoundingClientRect()
      const cx = Math.max(vr.left + 2, Math.min(vr.right - 2, x))
      const cy = Math.max(vr.top + 2, Math.min(vr.bottom - 2, y))
      const el = document.elementFromPoint(cx, cy)
      if (!el) return
      const cellEl = el.closest('.ag-cell') as HTMLElement | null
      if (!cellEl) return
      const colId = cellEl.getAttribute('col-id')
      if (!colId) return
      const rowEl = cellEl.closest('.ag-row') as HTMLElement | null
      if (!rowEl) return
      const rowIndex = parseInt(rowEl.getAttribute('row-index') ?? '-1', 10)
      if (rowIndex < 0) return
      const c = getColIdx(colId)
      if (c < 0) return
      const { row: sr, col: sc } = dragStart.current
      selRef.current = {
        r1: Math.min(sr, rowIndex),
        r2: Math.max(sr, rowIndex),
        c1: Math.min(sc, c),
        c2: Math.max(sc, c),
      }
      refreshSel()
    },
    [containerRef, getColIdx, refreshSel],
  )

  const startAutoScroll = useCallback(() => {
    if (autoScrollRef.current != null) return
    autoScrollRef.current = setInterval(() => {
      if (!dragging.current) {
        stopAutoScroll()
        return
      }
      const container = containerRef.current
      if (!container) return
      const { x, y } = mousePosRef.current
      const THRESHOLD = 60
      const BASE = 8
      const bodyViewport = container.querySelector('.ag-body-viewport') as HTMLElement | null
      if (bodyViewport) {
        const r = bodyViewport.getBoundingClientRect()
        if (y > r.bottom - THRESHOLD)
          bodyViewport.scrollTop += BASE + Math.floor((y - (r.bottom - THRESHOLD)) / 5)
        else if (y < r.top + THRESHOLD)
          bodyViewport.scrollTop -= BASE + Math.floor((r.top + THRESHOLD - y) / 5)
      }
      const centerViewport = container.querySelector(
        '.ag-center-cols-viewport',
      ) as HTMLElement | null
      if (centerViewport) {
        const r = centerViewport.getBoundingClientRect()
        if (x > r.right - THRESHOLD)
          centerViewport.scrollLeft += BASE + Math.floor((x - (r.right - THRESHOLD)) / 5)
        else if (x < r.left + THRESHOLD)
          centerViewport.scrollLeft -= BASE + Math.floor((r.left + THRESHOLD - x) / 5)
      }
      requestAnimationFrame(() => updateSelectionFromPos(x, y))
    }, 40)
  }, [containerRef, stopAutoScroll, updateSelectionFromPos])

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      mousePosRef.current = { x: e.clientX, y: e.clientY }
      if (!dragging.current || !dragStart.current) return
      const container = containerRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      const THRESHOLD = 60
      const nearEdge =
        e.clientX > rect.right - THRESHOLD ||
        e.clientX < rect.left + THRESHOLD ||
        e.clientY > rect.bottom - THRESHOLD ||
        e.clientY < rect.top + THRESHOLD
      if (nearEdge) startAutoScroll()
      else stopAutoScroll()
    }
    document.addEventListener('mousemove', onMouseMove)
    return () => document.removeEventListener('mousemove', onMouseMove)
  }, [containerRef, startAutoScroll, stopAutoScroll])

  // ── Shift+Arrow расширение выделения ───────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const editing = (gridRef.current?.api.getEditingCells() ?? []).length > 0
      if (
        e.shiftKey &&
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) &&
        !editing
      ) {
        e.preventDefault()
        e.stopPropagation()
        const focused = gridRef.current?.api.getFocusedCell()
        if (!focused || focused.rowIndex == null) return
        const focRow = focused.rowIndex
        const focCol = getColIdx(focused.column.getColId())
        if (!anchorRef.current) anchorRef.current = { row: focRow, col: focCol }
        const anchor = anchorRef.current
        let extRow = focRow
        let extCol = focCol
        const rowCount = gridRef.current?.api.getDisplayedRowCount() ?? 1
        if (e.key === 'ArrowUp') extRow = Math.max(0, extRow - 1)
        else if (e.key === 'ArrowDown') extRow = Math.min(rowCount - 1, extRow + 1)
        else if (e.key === 'ArrowLeft') extCol = Math.max(0, extCol - 1)
        else if (e.key === 'ArrowRight') {
          const total = gridRef.current?.api.getColumns()?.length ?? extCol + 1
          extCol = Math.min(total - 1, extCol + 1)
        }
        selRef.current = {
          r1: Math.min(anchor.row, extRow),
          r2: Math.max(anchor.row, extRow),
          c1: Math.min(anchor.col, extCol),
          c2: Math.max(anchor.col, extCol),
        }
        isShiftSelecting.current = true
        const targetCol = gridRef.current?.api.getColumns()?.[extCol]?.getColId()
        if (targetCol) gridRef.current?.api.setFocusedCell(extRow, targetCol)
        setTimeout(() => {
          isShiftSelecting.current = false
        }, 0)
        refreshSel()
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [getColIdx, gridRef, refreshSel])

  // ── Обработчики событий AG Grid ────────────────────────────────────────────

  const onCellMouseDown = useCallback(
    (e: CellMouseDownEvent<TableRow>) => {
      if (e.rowIndex == null) return
      const c = getColIdx(e.column.getColId())
      const isCtrl =
        (e.event as MouseEvent | undefined)?.ctrlKey || (e.event as MouseEvent | undefined)?.metaKey
      if (isCtrl) {
        const sel = selRef.current
        const next = new Set(extraCellsRef.current)
        if (sel) {
          for (let r = sel.r1; r <= sel.r2; r++)
            for (let cc = sel.c1; cc <= sel.c2; cc++) next.add(`${r}:${cc}`)
        }
        const key = `${e.rowIndex}:${c}`
        if (next.has(key)) next.delete(key)
        else next.add(key)
        extraCellsRef.current = next
        selRef.current = null
        refreshSel()
        return
      }
      const isShift = (e.event as MouseEvent | undefined)?.shiftKey
      if (isShift && anchorRef.current) {
        extraCellsRef.current = new Set()
        const { row: ar, col: ac } = anchorRef.current
        selRef.current = {
          r1: Math.min(ar, e.rowIndex),
          r2: Math.max(ar, e.rowIndex),
          c1: Math.min(ac, c),
          c2: Math.max(ac, c),
        }
        refreshSel()
        return
      }
      extraCellsRef.current = new Set()
      dragging.current = true
      dragStart.current = { row: e.rowIndex, col: c }
      anchorRef.current = { row: e.rowIndex, col: c }
      selRef.current = { r1: e.rowIndex, r2: e.rowIndex, c1: c, c2: c }
      refreshSel()
    },
    [getColIdx, refreshSel],
  )

  const onCellMouseOver = useCallback(
    (e: CellMouseOverEvent<TableRow>) => {
      if (!dragging.current || !dragStart.current || e.rowIndex == null) return
      const c = getColIdx(e.column.getColId())
      const { row: sr, col: sc } = dragStart.current
      selRef.current = {
        r1: Math.min(sr, e.rowIndex),
        r2: Math.max(sr, e.rowIndex),
        c1: Math.min(sc, c),
        c2: Math.max(sc, c),
      }
      refreshSel()
    },
    [getColIdx, refreshSel],
  )

  const onCellFocused = useCallback(
    (e: CellFocusedEvent) => {
      if (e.rowIndex == null) return
      const rowCount = gridRef.current?.api.getDisplayedRowCount() ?? 0
      onFocusAtEnd(e.rowIndex, rowCount)
      const wasCtrl = isCtrlSelecting.current
      isCtrlSelecting.current = false
      if (wasCtrl) return
      if (wasDraggingRef.current) {
        wasDraggingRef.current = false
        return
      }
      if (!isShiftSelecting.current) {
        const focused = gridRef.current?.api.getFocusedCell()
        if (focused) {
          const col = getColIdx(focused.column.getColId())
          anchorRef.current = { row: e.rowIndex, col }
          selRef.current = { r1: e.rowIndex, r2: e.rowIndex, c1: col, c2: col }
          refreshSel()
        }
      }
    },
    [getColIdx, gridRef, onFocusAtEnd, refreshSel],
  )

  return {
    selRef,
    extraCellsRef,
    anchorRef,
    dragStart,
    dragging,
    getColIdx,
    refreshSel,
    getCellStyle,
    onCellMouseDown,
    onCellMouseOver,
    onCellFocused,
  }
}
